import { AlertTriangle, Loader2 } from 'lucide-react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  /** Visual/semantic style of the confirm action. Defaults to `danger`. */
  variant?: 'danger' | 'primary';
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Reusable confirmation popup used for destructive/important actions.
 * Replaces the native `window.confirm` so the whole app shares the same look.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  loading = false,
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  const isDanger = variant === 'danger';
  const iconWrap = isDanger ? 'bg-danger/10' : 'bg-primary/10';
  const iconColor = isDanger ? 'text-danger' : 'text-primary';
  const confirmClasses = isDanger
    ? 'bg-danger text-white hover:bg-danger/90'
    : 'bg-primary text-white hover:bg-primary-dark';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!loading) onCancel();
      }}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg shrink-0 ${iconWrap}`}>
              <AlertTriangle size={20} className={iconColor} />
            </div>
            <div className="space-y-1 min-w-0">
              <h3 className="text-base font-semibold text-text">{title}</h3>
              {description && <div className="text-sm text-text-muted">{description}</div>}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2 ${confirmClasses}`}
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
